import Modal from "../../../../../components/fundamentals/modal";
import ModalH1TitleLayout from "../../../../../components/fundamentals/modal/layouts/header/ModalH1TitleLayout";
import {useForm} from "react-hook-form";
import InputField from "../../../../../components/form/input";
import InputHeader from "../../../../../components/form/input-header/input-header";
import Modal2ButtonsLayout from "../../../../../components/fundamentals/modal/layouts/footer/Modal2ButtonsLayout";
import {useCallback, useEffect, useState} from "react";
import adminService from "../../../../../services/admin.service";
import authService from "../../../../../services/auth.service";
import useNotification from "../../../../../hooks/use-notification";
import {Admin} from "../../../../../models/Admin";

type ModalAdminUserProps = {
    handleClose: () => void,
    postSubmit: () => Promise<void>,
    adminUser?: Admin,
}

export type AdminUserFormData = {
    email: string,
    firstname: string,
    lastname: string,
    function: string,
    role: string,
    can_export: boolean,
}
function ModalAdminUser({handleClose, postSubmit, adminUser}: ModalAdminUserProps) {

    const {
        register,
        formState: {errors, isValid},
        handleSubmit,
        setError
    } = useForm<AdminUserFormData>({
        mode: "onChange",
        defaultValues: {
            firstname: adminUser?.firstname ?? "",
            lastname: adminUser?.lastname ?? "",
            email: adminUser?.email ?? "",
            function: adminUser?.function ?? "",
            role: adminUser?.role ?? "admin",
            can_export: adminUser?.can_export ?? false,
        }
    });

    const notificationToast = useNotification();

    // RAG-35 : seul un super_admin peut attribuer un rôle et le droit d'export.
    // Pour les autres, ces champs restent masqués (l'API refuse de toute façon).
    const [canManageRoles, setCanManageRoles] = useState<boolean>(false);

    useEffect(() => {
        authService.me()
            .then((me) => setCanManageRoles(me.role === "super_admin"))
            .catch(() => setCanManageRoles(false));
    }, []);

    const onSubmit = useCallback(async (data: AdminUserFormData) => {
        try {

            adminUser
                ? await adminService.update(adminUser.id, data)
                : await adminService.create(data);

            notificationToast(
                "Succès",
                `L'administrateur a été ${adminUser ? "modifié" : "ajouté"}`,
                "success"
            );

            await postSubmit();
        } catch (e: any) {
            if (e.status) {
                for (const item of e.errors) {
                    item.key !== "unknown"
                        ? setError(item.key, {type: 'backend', message: item.message})
                        : notificationToast("Erreur", item.message, "error");
                }
            }
        }
    }, [])

    return (
        <Modal handleClose={handleClose}>
            <Modal.Body>
                <Modal.Header handleClose={handleClose}>
                    <ModalH1TitleLayout title={adminUser ? "Modifier l'utilisateur" : "Nouvel utilisateur"}/>
                </Modal.Header>
                <Modal.Content>
                    <div className="flex flex-col gap-y-6">
                        <div className="flex items-center gap-x-base">
                            <InputField
                                label="Prénom"
                                placeholder="Prénom"
                                required={true}
                                errors={errors}
                                {...register('firstname', {required: true})}
                            />
                            <InputField
                                label="Nom"
                                placeholder="Nom"
                                required={true}
                                errors={errors}
                                {...register('lastname', {required: true})}
                            />
                        </div>
                        <div className="flex items-center gap-x-base">
                            <InputField
                                label="Email"
                                placeholder="Email"
                                required={true}
                                errors={errors}
                                {...register('email', {
                                    required: true,
                                    pattern: {value: /^.+@.+\.\w{2,4}$/, message: "Email invalide"}
                                })}
                            />
                            <InputField
                                label="Fonction"
                                placeholder="Fonction"
                                errors={errors}
                                {...register('function')}
                            />
                        </div>
                        {canManageRoles && (
                            <div className="flex items-end gap-x-base">
                                <div className="w-full">
                                    <InputHeader label="Rôle" className="mb-xsmall" />
                                    <select
                                        className="w-full bg-grey-5 border border-gray-20 px-small py-xsmall rounded-rounded h-10 outline-none text-grey-90 font-normal focus:shadow-input focus:border-blue-60"
                                        {...register('role')}
                                    >
                                        <option value="super_admin">Super administrateur</option>
                                        <option value="admin">Administrateur</option>
                                        <option value="lecture">Lecture seule</option>
                                    </select>
                                </div>
                                <label className="w-full flex items-center gap-x-xsmall h-10 cursor-pointer select-none">
                                    <input type="checkbox" className="w-4 h-4" {...register('can_export')} />
                                    <span className="text-grey-90">Peut exporter les données (CSV)</span>
                                </label>
                            </div>
                        )}
                    </div>
                </Modal.Content>
                <Modal.Footer>
                    <Modal2ButtonsLayout
                        position="right"
                        discardButtonLabel="Fermer"
                        discardButtonHandler={handleClose}
                        submitButtonLabel="Enregistrer"
                        submitButtonHandler={handleSubmit(onSubmit)}
                        showSubmitButtonLoadingSpinner={false}
                        disableSubmitButton={!isValid}
                    />
                </Modal.Footer>
            </Modal.Body>
        </Modal>
    )
}

export default ModalAdminUser